import React, { useCallback, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { collection, getDocs } from "firebase/firestore";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { ATTENDANCE_BRANCHES } from "../../config/attendanceBranches";
import { getVisibleAttendanceBranches } from "../../config/appBranches";
import { resolveCollectionName } from "../../services/attendanceCollections";
import { db } from "../../firebase";
import { aggregateLateFirstPunchForMonth } from "../../helpers/payrollPunchAnalytics";
import { firstDefined, buildEmployeeCodeLookupForShop } from "../../helpers/payrollAttendanceCore";

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

export default function PayrollShiftAdherenceReport() {
  const storedUser = useMemo(() => getStoredUser(), []);
  const visibleBranches = useMemo(() => getVisibleAttendanceBranches(storedUser), [storedUser]);
  const branchPool = visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES;

  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [shopLabel, setShopLabel] = useState(branchPool[0]?.label || ATTENDANCE_BRANCHES[0].label);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState("");

  const currentShop = useMemo(
    () => branchPool.find((b) => b.label === shopLabel) || branchPool[0],
    [branchPool, shopLabel]
  );

  const shiftMonth = (delta) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

  const load = useCallback(async () => {
    if (!currentShop) return;
    setLoading(true);
    try {
      const punchCandidates = currentShop.punchCandidates || [];
      const punchName = (await resolveCollectionName(punchCandidates)) || punchCandidates[0];
      if (!punchName) throw new Error("Punch collection not found");

      const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
      const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 1);

      const empCol = await resolveCollectionName(currentShop.employeeCandidates || []);
      if (!empCol) throw new Error("Employees collection not found");
      const empSnap = await getDocs(collection(db, empCol));
      /** @type {Record<string, string>} */
      const expectedStartByEmpId = {};
      /** @type {Record<string, string>} */
      const nameById = {};
      empSnap.forEach((docSnap) => {
        const d = docSnap.data() || {};
        const id = String(
          firstDefined(d, ["employeeId", "empId", "uid", "user_id", "userId", "code", "employeeCode", "EmployeeId"]) ||
            docSnap.id
        );
        const exp = String(d.expectedStartTime ?? d.ExpectedStartTime ?? "").trim();
        if (exp) expectedStartByEmpId[id] = exp;
        nameById[id] = String(
          firstDefined(d, ["name", "employeeName", "fullName", "empName", "Name", "EmployeeName"]) || id
        );
      });

      const employeeCodeLookup = buildEmployeeCodeLookupForShop(currentShop, empSnap.docs);
      const { rows: stats, mode: loadMode } = await aggregateLateFirstPunchForMonth(
        punchName,
        monthStart,
        monthEnd,
        expectedStartByEmpId,
        employeeCodeLookup
      );
      setMode(loadMode);
      const merged = stats
        .map((r) => ({
          ...r,
          name: nameById[r.employeeId] || r.employeeId,
          expected: expectedStartByEmpId[r.employeeId] || "",
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setRows(merged);
      toast.success("Compared first punch to expected start");
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed");
    } finally {
      setLoading(false);
    }
  }, [currentShop, month]);

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-4xl px-4 py-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Payroll · Reports</p>
          <h1 className="text-2xl font-bold text-slate-900">Shift adherence (late first punch)</h1>
          <p className="mt-1 text-sm text-slate-600">
            Uses <strong>expectedStartTime</strong> on employee docs (HH:mm). First punch of the day after that time
            counts as late. Punch mode: <span className="font-mono">{mode || "—"}</span>
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-1 shadow-sm">
            <button type="button" onClick={() => shiftMonth(-1)} className="rounded-lg p-1 hover:bg-slate-100">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="min-w-[10rem] text-center text-sm font-semibold">
              {month.toLocaleString("en-IN", { month: "long", year: "numeric" })}
            </span>
            <button type="button" onClick={() => shiftMonth(1)} className="rounded-lg p-1 hover:bg-slate-100">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <label className="text-xs font-semibold text-slate-700">
            Branch
            <select
              value={shopLabel}
              onChange={(e) => setShopLabel(e.target.value)}
              className="mt-1 block min-w-[10rem] rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            >
              {branchPool.map((b) => (
                <option key={b.label} value={b.label}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Compute"}
          </button>
        </div>

        <div className="mt-6 overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-xs">
            <thead className="bg-slate-800 text-[10px] font-bold uppercase tracking-wide text-slate-100">
              <tr>
                <th className="px-2 py-2">Employee</th>
                <th className="px-2 py-2">Expected</th>
                <th className="px-2 py-2 text-right">Late days</th>
                <th className="px-2 py-2 text-right">Total late (min)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    Click Compute (employees need expected start times).
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.employeeId} className="border-t border-slate-100">
                    <td className="px-2 py-1.5">
                      <div className="font-medium">{r.name}</div>
                      <div className="font-mono text-[10px] text-slate-500">{r.employeeId}</div>
                    </td>
                    <td className="px-2 py-1.5 font-mono">{r.expected || "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.lateDays}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.totalLateMin}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
